import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {RadioButton} from '../../../../../libs/shared/ui/src/components/booleanInputs/radioButton';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {ChannelEmailThreadingModesEnum, ChannelTypesEnum} from '../../../../models/api/channelCommonTypes';
import {ChannelEmailModel} from '../../../../models/api/channelModel';
import {SettingsAccordionFieldLine} from '../../../common/settingsAccordionFieldLine';
import {SettingsCommonChannelFieldProps} from '../settingsCommonChannelFields';

/*
 * Props.
 */

interface SettingsThreadFieldProps extends SettingsCommonChannelFieldProps<ChannelEmailModel> {
  possibleValues: ReadonlyArray<ChannelEmailThreadingModesEnum>;
}

/*
 * Style.
 */

const StyledAnchor = styled.a`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.palette[PaletteColorsEnum.BLUE].shade40};
  cursor: pointer;

  &:hover {
    color: ${(p) => p.theme.palette[PaletteColorsEnum.BLUE].shade50};
  }
`;

const StyledRadioButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 10px;
  row-gap: 6px;
`;
const StyledOptionTitle = styled.div`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.medium};
  color: ${(p) => p.theme.greys.shade80};
`;
const StyledOptionSubtitle = styled.div`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const SettingsThreadField: FC<SettingsThreadFieldProps> = (props) => {
  const generalSubtitle = (
    <StyledAnchor href="https://help.front.com/t/k969pp">
      <Trans id="settingschannel-threadingfaq">View FAQ</Trans>
    </StyledAnchor>
  );
  const gmailSubtitle = (
    <Trans id="settingschannel-threadingfaq-gmail">
      Only Gmail threading will sync archived status and tags. {generalSubtitle}
    </Trans>
  );

  return (
    <SettingsAccordionFieldLine
      title={<Trans id="settingschannel-threadingmode">Threading mode</Trans>}
      subtitle={props.edited.type === ChannelTypesEnum.GMAIL ? gmailSubtitle : generalSubtitle}
      summaryValue={threadingModeRenderers[props.edited.preferences.threadingMode].title}
    >
      {props.possibleValues.map((threadingMode) => (
        <RadioButton
          key={threadingMode}
          onChange={() => {
            props.onPreferencesChange({
              ...props.edited.preferences,
              threadingMode,
            });
          }}
          isChecked={props.edited.preferences.threadingMode === threadingMode}
        >
          <StyledRadioButtonContent>
            <StyledOptionTitle>{threadingModeRenderers[threadingMode].title}</StyledOptionTitle>
            <StyledOptionSubtitle>{threadingModeRenderers[threadingMode].description}</StyledOptionSubtitle>
          </StyledRadioButtonContent>
        </RadioButton>
      ))}
    </SettingsAccordionFieldLine>
  );
};

const threadingModeRenderers: Record<
  ChannelEmailThreadingModesEnum,
  {title: ReactNode; description: ReactNode}
> = {
  [ChannelEmailThreadingModesEnum.GMAIL]: {
    title: <Trans id="threading-gmail-title">Gmail</Trans>,
    description: <Trans id="threading-gmail-subtitle">Thread messages based on Gmail logic.</Trans>,
  },
  [ChannelEmailThreadingModesEnum.OFFICE365]: {
    title: <Trans id="threading-office365-title">Office 365</Trans>,
    description: <Trans id="threading-office365-subtitle">Thread messages based on Office 365 logic.</Trans>,
  },
  [ChannelEmailThreadingModesEnum.REGULAR]: {
    title: <Trans id="threading-regular-title">Regular</Trans>,
    description: (
      <Trans id="threading-regular-subtitle">
        Thread messages when recipients explicitly respond to each other.
      </Trans>
    ),
  },
  [ChannelEmailThreadingModesEnum.SUBJECT]: {
    title: <Trans id="threading-subject-title">Regular + Subject-Recipient</Trans>,
    description: (
      <Trans id="threading-subject-subtitle">
        Thread messages with the same subject line and same recipients or using Regular logic.
      </Trans>
    ),
  },
  [ChannelEmailThreadingModesEnum.OFFICE365_SUBJECT]: {
    title: <Trans id="threading-office365_subject-title">Office 365 + Subject-Recipient</Trans>,
    description: (
      <Trans id="threading-office365_subject-subtitle">
        Thread messages using Office 365 and Subject-Recipient logic.
      </Trans>
    ),
  },
  [ChannelEmailThreadingModesEnum.SUBJECT_ONLY]: {
    title: <Trans id="threading-subject_only-title">Subject-Recipient</Trans>,
    description: (
      <Trans id="threading-subject_only-subtitle">
        Thread messages with the same subject line and same recipients.
      </Trans>
    ),
  },
  [ChannelEmailThreadingModesEnum.DISABLED]: {
    title: <Trans id="threading-disabled-title">No threading</Trans>,
    description: (
      <Trans id="threading-disabled-subtitle">Create a new conversation for every new message.</Trans>
    ),
  },
};
